/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosError } from 'axios';
import type { AnyAction } from 'redux-saga';
import { put } from 'redux-saga/effects';

import { displayToast } from '../redux/toast/toastSlice';
import { watchList } from '../redux/toast/watchList';
import i18n from '../utils/i18n';

export type ErrorType = {
	title?: string;
	message?: string;
	type?: 'error' | 'warning' | 'info' | 'success';
};

export function isAxiosError(candidate: unknown): candidate is AxiosError {
	if (
		candidate &&
		typeof candidate === 'object' &&
		'isAxiosError' in candidate
	) {
		return true;
	}
	return false;
}

const stringifyUnknownError = (error: unknown) => {
	if (typeof error !== 'object' || error === null) {
		return String(error);
	}

	try {
		return JSON.stringify(error);
	} catch {
		return String(error);
	}
};

export function* errorHandling(
	error: unknown | AxiosError,
	callback?: (payload: ErrorType) => AnyAction,
) {
	let output = '';
	let title = '';
	if (isAxiosError(error) || error instanceof AxiosError) {
		let message = error?.message ?? '';
		const responseData = error?.response?.data as
			| { title?: string; errors?: Record<string, string[]> }
			| null
			| undefined;

		const hasValidationErrors =
			responseData?.errors &&
			typeof responseData.errors === 'object' &&
			!Array.isArray(responseData.errors) &&
			Object.values(responseData.errors).every(
				(value) =>
					Array.isArray(value) &&
					value.every((item) => typeof item === 'string'),
			);

		if (responseData) {
			message = `${message}\r\n${JSON.stringify(responseData)}`;
		}
		if (responseData?.title) {
			message = `${responseData.title}\r\n\r\n${error?.message ?? ''}`;
		}
		if (hasValidationErrors && responseData?.errors) {
			const errorTexts = Object.values(responseData.errors).flat().join('\r\n');
			if (errorTexts) {
				message = `${message}\r\n\r\n${errorTexts}`;
			}
		}

		const configUrl = error?.config?.url;
		const baseURL = error?.config?.baseURL;
		const fullUrl =
			baseURL && configUrl ? `${baseURL}${configUrl}` : configUrl || baseURL;

		if (fullUrl) {
			message = `${message}\r\n\r\n${fullUrl}`;
		}

		output = message;
		title = responseData?.title ?? '';
	} else {
		output = `${i18n.t('global.unknownError')}: ${stringifyUnknownError(
			error,
		)}`;
		title = i18n.t('global.unknownError');
	}

	if (!callback) {
		yield put(
			displayToast({
				message: output,
				type: 'error',
			}),
		);
		return;
	}

	const isCallbackInWatchList = (
		watchList as unknown as Array<(payload: ErrorType) => AnyAction>
	).includes(callback);

	// The failure action must ALWAYS be dispatched — reducers clear their
	// loading flags from it; dropping it leaves grids and buttons stuck in a
	// loading state until the next successful request. The watchList only
	// decides who renders the toast: watched actions are toasted by
	// toastSaga, unwatched ones get the generic toast here.
	if (!isCallbackInWatchList) {
		yield put(
			displayToast({
				message: output,
				type: 'error',
			}),
		);
	}

	yield put(
		callback({
			title,
			message: output,
			type: 'error',
		}),
	);
}
